// Pure graph helpers for the agent Flow builder — sibling of
// settings/workflows/composables/useWorkflowGraph.js, adapted to the agent
// brain `workflow` block (10 node types, 3 edge types, permissive node.data).

export const NODE_TYPES = [
  'start',
  'end',
  'override_agent',
  'dispatch_tool',
  'agent_transfer',
  'transfer_to_number',
  'language_detection',
  'voicemail_detection',
  'play_keypad_touch_tone',
  'end_call',
];

export const EDGE_TYPES = ['unconditional', 'llm', 'expression'];

export const defaultNodeData = type => {
  switch (type) {
    case 'override_agent':
      return { label: '', additional_prompt: '', tool_ids: [] };
    case 'dispatch_tool':
      return { tool_id: '' };
    case 'agent_transfer':
      return { target_brain_id: null, condition: '' };
    case 'transfer_to_number':
      return { phone: '', condition: '' };
    default:
      return {};
  }
};

export const nextNodeId = nodes => {
  const max = nodes.reduce((acc, node) => {
    const match = /^n(\d+)$/.exec(node.id);
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 0);
  return `n${max + 1}`;
};

// Outgoing handles a node exposes; mirrors the Slice-2 runtime routing
// (dispatch_tool routes on success/failure; end is terminal).
export const sourceHandles = node => {
  if (node.type === 'dispatch_tool') return ['success', 'failure'];
  if (node.type === 'end') return [];
  return ['default'];
};

export const serializeGraph = (nodes, edges) => ({
  schema_version: 1,
  nodes: nodes.map(node => ({
    id: node.id,
    type: node.type,
    position: { x: node.position.x, y: node.position.y },
    data: node.data,
  })),
  edges: edges.map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    type: edge.type || 'unconditional',
    ...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
    ...(edge.condition ? { condition: edge.condition } : {}),
    ...(edge.expr ? { expr: edge.expr } : {}),
  })),
});

export const deserializeGraph = graph => ({
  nodes: (graph?.nodes || []).map(node => ({
    id: node.id,
    type: node.type,
    position: node.position || { x: 0, y: 0 },
    data: node.data || defaultNodeData(node.type),
  })),
  edges: (graph?.edges || []).map(edge => ({
    id:
      edge.id ||
      `e-${edge.source}-${edge.sourceHandle || 'default'}-${edge.target}`,
    source: edge.source,
    target: edge.target,
    type: edge.type || 'unconditional',
    sourceHandle: edge.sourceHandle || null,
    condition: edge.condition || '',
    expr: edge.expr || '',
  })),
});

// Reachability from the start node following edges.
const reachableFromStart = (nodes, edges) => {
  const startId = nodes.find(n => n.type === 'start')?.id;
  const reached = new Set();
  if (!startId) return reached;
  const stack = [startId];
  while (stack.length) {
    const id = stack.pop();
    if (reached.has(id)) continue;
    reached.add(id);
    edges.filter(e => e.source === id).forEach(e => stack.push(e.target));
  }
  return reached;
};

// Nodes from which an `end` is reachable (reverse reachability for termination lint).
const canReachEnd = (nodes, edges) => {
  const reaches = new Set(nodes.filter(n => n.type === 'end').map(n => n.id));
  let changed = true;
  while (changed) {
    changed = false;
    edges.forEach(e => {
      if (!reaches.has(e.source) && reaches.has(e.target)) {
        reaches.add(e.source);
        changed = true;
      }
    });
  }
  return reaches;
};

// Client-side hints mirroring backend publish validation; keys are i18n-able codes.
export const lintGraph = (nodes, edges, opts = {}) => {
  const issues = [];
  const toolNames = opts.toolNames || new Set();

  const starts = nodes.filter(n => n.type === 'start');
  if (starts.length !== 1) issues.push('START_REQUIRED');
  if (!nodes.some(n => n.type === 'end')) issues.push('END_REQUIRED');

  const reached = reachableFromStart(nodes, edges);
  const reachesEnd = nodes.some(n => n.type === 'end' && reached.has(n.id));
  if (nodes.length > 1 && !reachesEnd) issues.push('NO_PATH_TO_END');

  // Slice 5: a reachable agent_transfer that can never reach an end is a transfer cycle.
  const reachesEndFrom = canReachEnd(nodes, edges);
  if (
    nodes.some(
      n =>
        n.type === 'agent_transfer' &&
        reached.has(n.id) &&
        !reachesEndFrom.has(n.id)
    )
  ) {
    issues.push('TRANSFER_CYCLE');
  }

  if (
    nodes.length > 1 &&
    nodes.some(n => n.type !== 'start' && !reached.has(n.id))
  ) {
    issues.push('ORPHAN_NODES');
  }

  nodes.forEach(node => {
    if (node.type === 'dispatch_tool' && !node.data.tool_id) {
      issues.push('DISPATCH_TOOL_MISSING_TOOL');
    }
    if (node.type === 'agent_transfer' && !node.data.target_brain_id) {
      issues.push('AGENT_TRANSFER_MISSING_TARGET');
    }
    if (node.type === 'override_agent') {
      const refs = node.data.tool_ids || [];
      if (refs.some(name => !toolNames.has(name))) {
        issues.push('UNSCOPED_TOOL_REF');
      }
    }
    const unconditional = edges.filter(
      e => e.source === node.id && (e.type || 'unconditional') === 'unconditional'
    );
    if (unconditional.length > 1) issues.push('AMBIGUOUS_EDGES');
  });

  return [...new Set(issues)];
};

// Returns a new draft_config: empty canvas → workflow key removed (flat mode);
// otherwise workflow = serialized graph.
export const buildDraftConfig = (draftConfig, nodes, edges) => {
  const next = { ...(draftConfig || {}) };
  if (!nodes.length) {
    delete next.workflow;
    return next;
  }
  next.workflow = serializeGraph(nodes, edges);
  return next;
};
